import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import { ControlledAutocompleteDropdownMultiple } from 'src/shared/components/form/controlledAutocompleteDropdownMultiple';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { HELP_MEDIA_BASE_URL } from 'src/modules/help/functions/resolveHelpMediaUrls';
import { HELP_EDITOR_CONTENT_STYLE } from 'src/modules/help/functions/helpCalloutStyle';
import { HelpPageCategory } from '../models/helpPageCategory';
import { HelpPageModule } from '../models/helpPageModule';
import { getAllHelpPageModules } from '../services/helpPageModuleService';
import { MAX_HELP_IMAGE_SIZE, MAX_HELP_VIDEO_SIZE, uploadHelpPageMedia, uploadHelpPageVideo } from '../services/helpPageMediaService';
import { authorityOptions } from '../functions/getAuthorityLabel';
import { slugify } from '../functions/slugify';
import { getCategoryOptions } from '../functions/getCategoryOptions';
import { getHelpPageTermFields } from '../functions/helpPageTerms';

export type HelpArticleFormValues = {
  title: string;
  slug: string;
  categoryId: number;
  requiredAuthority: number;
  modules: HelpPageModule[];
  bodyHtml: string;
  changeSummary: string;
};

type HelpArticleFormProps = {
  defaultValues: HelpArticleFormValues;
  categories: HelpPageCategory[];
  isEdit: boolean;
  saving: boolean;
  /** Arkiverad artikel: alla fält avstängda och inga knappar. */
  readOnly?: boolean;
  onSaveDraft: (values: HelpArticleFormValues) => void;
  onPublish: (values: HelpArticleFormValues) => void;
  /** Visar Arkivera-knappen (anroparen skickar den bara för en befintlig artikel). */
  onArchive?: () => void;
};

// Felet loggas och väljaren får en tom lista.
const fetchModules = async (): Promise<HelpPageModule[]> => {
  try {
    return (await getAllHelpPageModules()).data;
  } catch (e) {
    ConsoleHelper.log(e);
  }

  return [];
};

export const HelpArticleForm = (props: HelpArticleFormProps) => {
  const { defaultValues, categories, isEdit, saving, readOnly = false, onSaveDraft, onPublish, onArchive } = props;
  const { t } = useTranslation(['helpArticles', 'common']);
  const { currentUser } = useGlobals();
  const termFields = useMemo(() => getHelpPageTermFields(currentUser?.mainSettings ?? []), [currentUser?.mainSettings]);

  const schema = yup.object().shape({
    title: yup
      .string()
      .max(300, t('common:validationMessages.maxLength', { count: 300 }))
      .required(t('common:validationMessages.required')),
    // Samma regel som API:t: sluggen blir en del av en länk.
    slug: yup
      .string()
      .max(200, t('common:validationMessages.maxLength', { count: 200 }))
      .matches(/^[a-z0-9]+(-[a-z0-9]+)*$/, { message: t('helpArticles:form.slugInvalid'), excludeEmptyString: true })
      .required(t('common:validationMessages.required')),
    categoryId: yup.number().min(1, t('common:validationMessages.required')).required(t('common:validationMessages.required')),
    requiredAuthority: yup.number().required(t('common:validationMessages.required')),
    bodyHtml: yup.string().required(t('common:validationMessages.required')),
    changeSummary: yup.string().max(500, t('common:validationMessages.maxLength', { count: 500 })),
  });

  const formMethods = useForm<HelpArticleFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields },
  } = formMethods;

  // Nya artiklar får sitt länknamn föreslaget från titeln, tills användaren skriver ett eget.
  const titleValue = watch('title');
  useEffect(() => {
    if (!isEdit && !dirtyFields.slug) {
      setValue('slug', slugify(titleValue || ''));
    }
  }, [titleValue, isEdit, dirtyFields.slug, setValue]);

  // En arkiverad artikel kan ligga i en kategori som raderats; utan ett eget val skulle fältet stå tomt. Bara i läsläget,
  // så att en saknad kategori aldrig går att välja och spara.
  const categoryOptions = useMemo(() => {
    const options = getCategoryOptions(categories).map((c) => ({ label: c.name, value: c.id }));
    const { categoryId } = defaultValues;
    if (readOnly && categoryId > 0 && !options.some((o) => o.value === categoryId)) {
      options.push({ label: t('helpArticles:form.deletedCategory'), value: categoryId });
    }
    return options;
  }, [categories, defaultValues, readOnly, t]);

  // Interceptorn avvisar med svaret. Bara 400 har ett meddelande till användaren (fel filtyp, för stor fil); andra fel
  // kan ha en stacktrace eller en HTML-sida som svar.
  const uploadErrorMessage = (e: unknown) => {
    ConsoleHelper.log(e);
    const response = e as { status?: number; data?: unknown };
    return response?.status === 400 && typeof response.data === 'string' && response.data ? response.data : t('helpArticles:form.uploadTryAgain');
  };

  // Bilderna laddas upp till hjälpartiklarnas egen lagring istället för husets bildarkiv. Felet visas i editorn, som
  // själv skriver "Kunde inte ladda upp bild:" framför.
  const uploadImage = async (file: File): Promise<string> => {
    if (file.size > MAX_HELP_IMAGE_SIZE) throw new Error(t('helpArticles:form.imageTooLarge'));

    try {
      return (await uploadHelpPageMedia(file)).data.location;
    } catch (e) {
      throw new Error(uploadErrorMessage(e));
    }
  };

  // Videorna laddas upp på samma sätt, med förloppet i editorn. Hela meddelandet skrivs här, eftersom editorn visar det
  // som det är.
  const uploadVideo = async (file: File, onProgress: (percent: number) => void): Promise<string> => {
    if (file.size > MAX_HELP_VIDEO_SIZE) throw new Error(t('helpArticles:form.videoUploadFailed', { message: t('helpArticles:form.videoTooLarge') }));

    try {
      return (await uploadHelpPageVideo(file, onProgress)).data.location;
    } catch (e) {
      throw new Error(t('helpArticles:form.videoUploadFailed', { message: uploadErrorMessage(e) }));
    }
  };

  const formId = 'help-article-form';

  return (
    <FormProvider {...formMethods}>
      <Box component='form' id={formId} onSubmit={handleSubmit(onSaveDraft)} sx={{ position: 'relative', pb: 4 }}>
        <LoadingOverlay loading={saving} />
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mx: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <ControlledTextField label={t('helpArticles:form.articleTitle')} name='title' disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ControlledTextField label={t('helpArticles:form.slug')} name='slug' disabled={isEdit} helperText={t('helpArticles:form.slugHelp')} />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledSelectBox items={categoryOptions} name='categoryId' label={t('helpArticles:form.category')} disabled={readOnly} />
              </Grid>
              <Grid item xs={12} md={4}>
                <ControlledSelectBox
                  items={authorityOptions(t).map((option) => ({ label: option.name, value: option.id }))}
                  name='requiredAuthority'
                  label={t('helpArticles:form.authority')}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ControlledAutocompleteDropdownMultiple<HelpPageModule>
                  name='modules'
                  nameProp='name'
                  keyProp='id'
                  label={t('helpArticles:form.modules')}
                  fetchMethod={fetchModules}
                  callback={() => undefined}
                  componentWidth='100%'
                  disabled={readOnly}
                  // Ett avstängt fält kan inte få fokus och fällas ut, så i läsläget visas alla taggar direkt.
                  limitTags={readOnly ? -1 : undefined}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant='h6' sx={{ mt: 0, mb: 2, ml: 3 }}>
              {t('helpArticles:form.body')}
            </Typography>
            <ControlledTextEditor
              name='bodyHtml'
              customFields={termFields}
              customFieldsTitle={t('helpArticles:form.termsMenu')}
              rows={30}
              disabled={readOnly}
              imageUploadHandler={uploadImage}
              imageFileTypes='jpg,jpeg,png,gif,webp'
              videoUploadHandler={uploadVideo}
              videoButtonText={t('helpArticles:form.insertVideo')}
              videoUploadingText={t('helpArticles:form.videoUploading')}
              // Inga inbäddade spelare från andra sajter (t.ex. YouTube): de kan sätta kakor och spåra läsarna.
              invalidElements='iframe,object,embed'
              // Bildernas adress sparas relativt API:t; så här syns de medan man skriver.
              documentBaseUrl={HELP_MEDIA_BASE_URL}
              // Citatrutorna ser ut som i hjälpcentret medan man skriver.
              contentStyle={HELP_EDITOR_CONTENT_STYLE}
            />
            <Divider sx={{ mt: 0, mb: 2 }} />
          </Grid>

          {/* Ändringsnotering */}
          <Grid item xs={12} md={7}>
            {/* Boxen inuti Grid-item hanterar indraget utan att spräcka layouten */}
            <Box sx={{ ml: 3 }}>
              <ControlledTextField
                label={t('helpArticles:form.changeSummary')}
                name='changeSummary'
                placeholder={t('helpArticles:form.changeSummaryPlaceholder')}
                disabled={readOnly}
              />
            </Box>
          </Grid>

          {/* Knappar. Arkivera ligger först, skild från Spara och Publicera. */}
          {!readOnly && (
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 3, pr: 4, mt: { xs: 2, md: 0 } }}
            >
              {onArchive && (
                <Button variant='outlined' color='error' disabled={saving} onClick={onArchive} sx={{ mr: 'auto' }}>
                  {t('helpArticles:form.archive')}
                </Button>
              )}
              <Button type='submit' form={formId} variant='outlined' color='primary' disabled={saving} disableElevation>
                {t('helpArticles:form.saveDraft')}
              </Button>
              <Button variant='contained' color='primary' disabled={saving} disableElevation onClick={handleSubmit(onPublish)}>
                {t('helpArticles:form.publish')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </FormProvider>
  );
};
